import {
  Admin as AdminSdk,
  GlobalAdmin as sdkGlobalAdmin,
  Device as DeviceSdk,
} from "coolremote-sdk";
import { Action, action, computed, Computed, Thunk, thunk } from "easy-peasy";
import _ from "lodash";
import { localXhr } from "../services/localXhr";

export interface IDeviceModel {
  id: string;
  serial: string;
  pin?: string;
  name: string;
  isConnected: boolean;
  site: string;
  siteName?: string;
  units: string[];
  systems: string[];
  role: any;
  connected?: boolean;
  isRegistered: boolean;
  firmwareVersion?: any;
  customer?: any;
  customerId?: any;
  hvacLines?: any;
  deviceType?: number;
  disconnectedTimestamp?: number;
  connectedTimestamp?: number;
  account?: string;
  type?: any;
}

export interface IDeviceMap {
  [key: string]: IDeviceModel;
}

export interface IDevicesModel {
  allDevices: IDeviceMap;
  devices: any;
  connectedDevices: string[];
  setDevices: Action<IDevicesModel, { devices: any }>;
  setConnectedDevices: Action<IDevicesModel, string[]>;
  getDevices: Thunk<IDevicesModel>;
  getDevicesPackages: Thunk<IDevicesModel>;
  getDevicesBySerial: Thunk<IDevicesModel, { serial: string }>;
  getDevicesAssociations: Thunk<IDevicesModel>;
  deleteDevice: Thunk<IDevicesModel, string>;
  deletePackage: Thunk<IDevicesModel, string>;
  createPackage: Thunk<IDevicesModel, { data: any }>;
  updatePackage: Thunk<IDevicesModel, { id: string; data: any }>;
  removeDeletedDevice: Action<IDevicesModel, string>;
  getConnectedDevices: Thunk<IDevicesModel>;
  getDeviceWithState: Computed<IDevicesModel, any>;
  getDeviceMetaDate: Thunk<IDevicesModel, string>;
  chosenDeviceMetaData: any;
  setChosenDeviceMetaData: Action<IDevicesModel, any>;
  bootDevice: Thunk<IDevicesModel, string>;
  updateDevice: Thunk<IDevicesModel, unknown>;
  getDeviceById: Thunk<IDevicesModel, string>;
  controlDevice: Thunk<IDevicesModel, { deviceId: string; command: any }>;
  upgradeFirmware: Thunk<IDevicesModel, { deviceId: string; command: any }>;
  firmwareUpdateByVersion: Thunk<
    IDevicesModel,
    { deviceId: string; firmwareVersionId: string }
  >;
  cancelFirmwareUpdate: Thunk<
    IDevicesModel,
    { deviceId: string; actionId?: string | number; force?: boolean }
  >;
  getFirmwareInstalled: Thunk<IDevicesModel, string>;
  getFirmwareActions: Thunk<
    IDevicesModel,
    { deviceId: string; offset?: number; limit?: number; sort?: string }
  >;
  getFirmwareAction: Thunk<
    IDevicesModel,
    { deviceId: string; actionId: string | number }
  >;
  forceFirmwareAction: Thunk<
    IDevicesModel,
    { deviceId: string; actionId: string | number; forceType?: string }
  >;
  firmwareStatus: Thunk<IDevicesModel, { deviceId: string }>;
  getDeviceName: Computed<
    IDevicesModel,
    (id: string | undefined) => string | undefined
  >;
  convertUnitNames: Thunk<IDevicesModel, { siteId: string }>;
  convertZonesToGroups: Thunk<IDevicesModel, { siteId: string }>;
  getCategories: Thunk<IDevicesModel, { deviceId: string }>;
  getConfigCategory: Thunk<IDevicesModel, { deviceId: string; category: string }>;
  setConfigCategory: Thunk<IDevicesModel, { deviceId: string; category: string, data: any }>;
  allDebugDevices: any;
  setAllDebugDevices: Action<IDevicesModel, any>;
  getAllDebugDevices: Thunk<IDevicesModel>;
  createDebugDevice: Thunk<IDevicesModel, { serial: string; comment: string }>;
  editDebugDevice: Thunk<IDevicesModel, { serial: string; comment: string }>;
  deleteDebugDevice: Thunk<IDevicesModel, string>;
  allBlockedDevices: any;
  setAllBlockedDevices: Action<IDevicesModel, any>;
  getAllBlockedDevices: Thunk<IDevicesModel>;
  createBlockedDevice: Thunk<IDevicesModel, { serial: string; notes: string }>;
  editBlockedDevice: Thunk<IDevicesModel, { serial: string; notes: string }>;
  deleteBlockedDevice: Thunk<IDevicesModel, string>;
  getDeviceCache: Thunk<IDevicesModel, string>;
  clearDeviceCache: Thunk<IDevicesModel, { id: string; line?: number }>;
  allMockDevices: any;
  setAllMockDevices: Action<IDevicesModel, any>;
  getAllMockDevices: Thunk<IDevicesModel>;
  createMockDevice: Thunk<IDevicesModel, { name: string; serial: string; pin?: string; simulatorConnected?: boolean }>;
  updateMockDevice: Thunk<IDevicesModel, { id: string; name?: string }>;
  deleteMockDevice: Thunk<IDevicesModel, string>;
  setMockDeviceConnection: Thunk<IDevicesModel, { id: string; connected: boolean }>;
  setMockDeviceRegistration: Thunk<IDevicesModel, { id: string; registered: boolean }>;
  setMockDeviceBlocked: Thunk<IDevicesModel, { id: string; blocked: boolean }>;
  setMockDeviceProLicense: Thunk<IDevicesModel, { id: string; proLicense: boolean }>;
  checkMockSerialAvailable: Thunk<IDevicesModel, string>;
  generateMockSerial: Thunk<IDevicesModel>;
  resetAllMockDevices: Thunk<IDevicesModel>;
  mockDeviceHierarchy: any;
  setMockDeviceHierarchy: Action<IDevicesModel, { deviceId: string; hierarchy: any }>;
  getMockDeviceHierarchy: Thunk<IDevicesModel, string>;
  createMockHierarchyItem: Thunk<IDevicesModel, { deviceId: string; type: string; data: any }>;
  updateMockHierarchyItem: Thunk<IDevicesModel, { deviceId: string; type: string; subId: string; data: any }>;
  deleteMockHierarchyItem: Thunk<IDevicesModel, { deviceId: string; type: string; subId: string }>;
}

export const DevicesModel: IDevicesModel = {
  allDevices: {},
  devices: {},
  connectedDevices: [],
  chosenDeviceMetaData: {},
  allDebugDevices: {},
  setAllDebugDevices: action((state, payload) => {
    state.allDebugDevices = payload;
  }),
  allBlockedDevices: {},
  setAllBlockedDevices: action((state, payload) => {
    state.allBlockedDevices = payload;
  }),
  setConnectedDevices: action((state, payload) => {
    state.connectedDevices = payload;
  }),

  setDevices: action((state, payload) => {
    state.devices = payload;
    const fetchedDevices: IDeviceMap = _(Object.values(payload.devices))
      .map((device: any) => {
        const addedDevice: IDeviceModel = { ...device };

        return addedDevice;
      })
      .keyBy("id")
      .value();

    state.allDevices = fetchedDevices;
  }),

  setChosenDeviceMetaData: action((state, payload) => {
    state.chosenDeviceMetaData = payload.data;
  }),

  getDevices: thunk(async actions => {
    return AdminSdk.getDevicesTableData()
      .then((data: any) => {
        actions.setDevices({ devices: data });
        return true;
      })
      .catch(() => false);
  }),

  getDevicesPackages: thunk(async actions => {
    return DeviceSdk.getDevicesPackages();
  }),

  getDevicesBySerial: thunk(async (actions, payload) => {
    return sdkGlobalAdmin.getDevicesBySerial(payload.serial);
  }),

  getDevicesAssociations: thunk(async actions => {
    return DeviceSdk.getDevicesAssociations();
  }),

  getConnectedDevices: thunk(async actions => {
    return localXhr
      .get(`${process.env.REACT_APP_ADMIN_SERVER_URL}/devices`)
      .then((data: any) => data.json())
      .then((data: any) => {
        actions.setConnectedDevices(data.data);
        return true;
      })
      .catch(e => {
        if (_.includes(e.message, "Failed to fetch")) {
          throw new Error("Failed to fetch from metaData server");
        }
        throw new Error(e);
      });
  }),

  getDeviceMetaDate: thunk(async (actions, payload) => {
    return localXhr
      .get(`${process.env.REACT_APP_ADMIN_SERVER_URL}/deviceMeta/${payload}`)
      .then((data: any) => data.json())
      .then((data: any) => {
        if (!data.success) {
          throw new Error(data.data);
        }

        actions.setChosenDeviceMetaData(data);

        return true;
      })
      .catch(e => {
        if (_.includes(e.message, "Failed to fetch")) {
          throw new Error("Failed to fetch from metaData server");
        }
        throw new Error(e);
      });
  }),

  getDeviceWithState: computed(state => {
    if (_.isEmpty(state.allDevices)) {
      return {};
    }

    const allDevicesWithState = _.reduce(
      state.allDevices,
      (obj: any, device: IDeviceModel) => {
        obj[device.serial] = { ...device, ...{ connected: false } };
        return obj;
      },
      {}
    );

    _.forEach(state.connectedDevices, deviceId => {
      if (!allDevicesWithState[deviceId]) {
        return;
      }

      allDevicesWithState[deviceId].connected = true;
    });
    return allDevicesWithState;
  }),

  bootDevice: thunk(async (actions, payload) => {
    return localXhr
      .get(
        `${process.env.REACT_APP_ADMIN_SERVER_URL}/deviceMeta/boot/${payload}`
      )
      .then((data: any) => data.json())
      .then((data: any) => {
        if (!data.success) {
          throw new Error(data.data);
        }
        return true;
      })
      .catch(e => {
        if (_.includes(e.message, "Failed to fetch")) {
          return new Error("Failed to fetch from metaData server");
        }
        return new Error(e);
      });
  }),

  updateDevice: thunk(async (actions, payload) => {
    return localXhr
      .post(payload, `${process.env.REACT_APP_ADMIN_SERVER_URL}/deviceMeta`)
      .then((data: any) => data.json())
      .then((data: any) => {
        if (!data.success) {
          throw new Error(data.data);
        }
        return true;
      })
      .catch(e => {
        if (_.includes(e.message, "Failed to fetch")) {
          throw new Error("Failed to fetch from metaData server");
        }
        throw new Error(e);
      });
  }),
  getDeviceById: thunk(async (actions, payload) => {
    return sdkGlobalAdmin.getDeviceById(payload);
  }),
  controlDevice: thunk(async (actions, payload) => {
    const { deviceId: id, command } = payload;
    return sdkGlobalAdmin.controlDevice(id, command);
  }),

  upgradeFirmware: thunk(async (actions, payload) => {
    const { deviceId: id, command } = payload;
    return sdkGlobalAdmin.upgradeFirmware(id, command);
  }),

  firmwareUpdateByVersion: thunk(async (actions, payload) => {
    const { deviceId, firmwareVersionId } = payload;
    if (!sdkGlobalAdmin.firmwareUpdate) {
      throw new Error("firmwareUpdate is not available in coolremote-sdk");
    }
    return sdkGlobalAdmin.firmwareUpdate(String(deviceId), String(firmwareVersionId));
  }),

  cancelFirmwareUpdate: thunk(async (actions, payload) => {
    const { deviceId, actionId, force } = payload;
    return sdkGlobalAdmin.cancelFirmwareUpdate(deviceId, { actionId, force });
  }),

  getFirmwareInstalled: thunk(async (actions, payload) => {
    return sdkGlobalAdmin.getFirmwareInstalled(payload);
  }),

  getFirmwareActions: thunk(async (actions, payload) => {
    const { deviceId, offset, limit, sort } = payload;
    return sdkGlobalAdmin.getFirmwareActions(deviceId, { offset, limit, sort });
  }),

  getFirmwareAction: thunk(async (actions, payload) => {
    const { deviceId, actionId } = payload;
    return sdkGlobalAdmin.getFirmwareAction(deviceId, actionId);
  }),

  forceFirmwareAction: thunk(async (actions, payload) => {
    const { deviceId, actionId, forceType } = payload;
    return sdkGlobalAdmin.forceFirmwareAction(deviceId, actionId, forceType || "forced");
  }),

  firmwareStatus: thunk(async (actions, payload) => {
    const id = payload;
    return sdkGlobalAdmin.firmwareStatus(id);
  }),

  getDeviceName: computed(state => id => {
    const noName = "-";

    if (!id) {
      return noName;
    }

    if (!state.allDevices[id]) {
      return noName;
    }

    return state.allDevices[id].name ? state.allDevices[id].name : noName;
  }),
  deleteDevice: thunk((actions, payload) => {
    DeviceSdk.delete(payload).then(() => {
      actions.removeDeletedDevice(payload);
    });
  }),
  deletePackage: thunk((actions, payload) => {
    return DeviceSdk.deletePackage(payload);
  }),
  createPackage: thunk((actions, payload) => {
    return DeviceSdk.createPackage(payload.data);
  }),
  updatePackage: thunk((actions, payload) => {
    return DeviceSdk.updatePackage(payload.id, payload.data);
  }),
  removeDeletedDevice: action((state, payload) => {
    const devicesMap = { ...state.allDevices };
    delete devicesMap[payload];
    state.allDevices = { ...devicesMap };
  }),
  convertUnitNames: thunk(async (actions, payload) => {
    return sdkGlobalAdmin.convertUnitNames(payload);
  }),
  convertZonesToGroups: thunk(async (actions, payload) => {
    return sdkGlobalAdmin.convertZonesToGroups(payload);
  }),
  getCategories: thunk(async (actions, payload) => {
    return DeviceSdk.getCategories(payload);
  }),
  getConfigCategory: thunk(async (actions, payload) => {
    const { deviceId, category } = payload;
    return DeviceSdk.getConfigCategory(deviceId, category);
  }),
  setConfigCategory: thunk(async (actions, payload) => {
    const { deviceId, category, data } = payload;
    return DeviceSdk.setConfigCategory(deviceId, category, data);
  }),

  getAllDebugDevices: thunk(async (actions) => {
    return AdminSdk.getDebugDevices()
      .then((response: any) => {
        const debugDevicesMap = _.keyBy(response, "serial");
        actions.setAllDebugDevices(debugDevicesMap);
        return true;
      })
      .catch(() => false);
  }),

  createDebugDevice: thunk(async (actions, payload) => {
    await AdminSdk.createDebugDevice(payload);
    actions.getAllDebugDevices();
  }),

  editDebugDevice: thunk(async (actions, payload) => {
    await AdminSdk.updateDebugDeviceComment(payload.serial, { comment: payload.comment });
    actions.getAllDebugDevices();
  }),

  deleteDebugDevice: thunk(async (actions, payload) => {
    await AdminSdk.deleteDebugDevice(payload);
    actions.getAllDebugDevices();
  }),

  getAllBlockedDevices: thunk(async (actions) => {
    return AdminSdk.getDeviceBlacklist()
      .then((response: any) => {
        const blockedDevicesMap = _.keyBy(response, "serial");
        actions.setAllBlockedDevices(blockedDevicesMap);
        return true;
      })
      .catch(() => false);
  }),

  createBlockedDevice: thunk(async (actions, payload) => {
    await AdminSdk.createDeviceBlacklist(payload);
    actions.getAllBlockedDevices();
  }),

  editBlockedDevice: thunk(async (actions, payload) => {
    await AdminSdk.updateDeviceBlacklist(payload.serial, { notes: payload.notes });
    actions.getAllBlockedDevices();
  }),

  deleteBlockedDevice: thunk(async (actions, payload) => {
    await AdminSdk.deleteDeviceBlacklist(payload);
    actions.getAllBlockedDevices();
  }),

  getDeviceCache: thunk(async (actions, id) => {
    return sdkGlobalAdmin.getDeviceCache(id);
  }),

  clearDeviceCache: thunk(async (actions, { id, line }) => {
    return sdkGlobalAdmin.clearDeviceCache(id, line);
  }),

  allMockDevices: {},
  setAllMockDevices: action((state, payload) => {
    state.allMockDevices = payload;
  }),

  getAllMockDevices: thunk(async (actions) => {
    return AdminSdk.getMockDevices()
      .then((response: any) => {
        const mockDevicesMap = _.keyBy(response, "id");
        actions.setAllMockDevices(mockDevicesMap);
        return true;
      })
      .catch(() => false);
  }),

  createMockDevice: thunk(async (actions, payload) => {
    await AdminSdk.createMockDevice(payload);
    actions.getAllMockDevices();
  }),

  updateMockDevice: thunk(async (actions, payload) => {
    const { id, ...data } = payload;
    await AdminSdk.updateMockDevice(id, data);
    actions.getAllMockDevices();
  }),

  deleteMockDevice: thunk(async (actions, payload) => {
    await AdminSdk.deleteMockDevice(payload);
    actions.getAllMockDevices();
  }),

  setMockDeviceConnection: thunk(async (actions, payload) => {
    const { id, connected } = payload;
    await AdminSdk.setMockDeviceConnection(id, { connected });
    actions.getAllMockDevices();
  }),

  setMockDeviceRegistration: thunk(async (actions, payload) => {
    const { id, registered } = payload;
    await AdminSdk.setMockDeviceRegistration(id, { registered });
    actions.getAllMockDevices();
  }),

  setMockDeviceBlocked: thunk(async (actions, payload) => {
    const { id, blocked } = payload;
    await AdminSdk.setMockDeviceBlocked(id, { blocked });
    actions.getAllMockDevices();
  }),

  setMockDeviceProLicense: thunk(async (actions, payload) => {
    const { id, proLicense } = payload;
    await AdminSdk.setMockDeviceProLicense(id, { proLicense });
    actions.getAllMockDevices();
  }),

  checkMockSerialAvailable: thunk(async (actions, serial) => {
    return AdminSdk.checkMockDeviceSerial(serial);
  }),

  generateMockSerial: thunk(async (actions) => {
    return AdminSdk.generateMockDeviceSerial();
  }),

  resetAllMockDevices: thunk(async (actions) => {
    await AdminSdk.cleanupMockDevices();
    actions.getAllMockDevices();
  }),

  mockDeviceHierarchy: {},
  setMockDeviceHierarchy: action((state, payload) => {
    const { deviceId, hierarchy } = payload;
    state.mockDeviceHierarchy = {
      ...state.mockDeviceHierarchy,
      [deviceId]: hierarchy,
    };
  }),

  getMockDeviceHierarchy: thunk(async (actions, deviceId) => {
    return AdminSdk.getMockDeviceById(deviceId)
      .then((response: any) => {
        actions.setMockDeviceHierarchy({ deviceId, hierarchy: response.hierarchy });
        return true;
      })
      .catch(() => false);
  }),

  createMockHierarchyItem: thunk(async (actions, payload) => {
    const { deviceId, type, data } = payload;
    await AdminSdk.createMockDeviceSubItem(deviceId, type, data);
    actions.getMockDeviceHierarchy(deviceId);
  }),

  updateMockHierarchyItem: thunk(async (actions, payload) => {
    const { deviceId, type, subId, data } = payload;
    await AdminSdk.updateMockDeviceSubItem(deviceId, type, subId, data);
    actions.getMockDeviceHierarchy(deviceId);
  }),

  deleteMockHierarchyItem: thunk(async (actions, payload) => {
    const { deviceId, type, subId } = payload;
    await AdminSdk.deleteMockDeviceSubItem(deviceId, type, subId);
    actions.getMockDeviceHierarchy(deviceId);
  }),
};
